/**
 * Create-ad owner registration carries the publish payload in router state
 * (`/create-ad/register-owner`). After publish, `listingId` continues the flow
 * via URL search params (`/create-ad/published?listingId=…`, `/account?listingId=…`).
 *
 * Owner-intent is in-memory only for the SPA session (never sessionStorage).
 */

let listingOwnerIntent = false
const listeners = new Set<() => void>()

function notifyIntentListeners() {
  listeners.forEach(listener => listener())
}

/** Subscribe to owner-intent changes (for header role re-renders). */
export const subscribeListingOwnerIntent = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const markListingOwnerIntent = () => {
  listingOwnerIntent = true
  notifyIntentListeners()
}

export const hasListingOwnerIntent = (): boolean => listingOwnerIntent

export const clearListingOwnerIntent = () => {
  listingOwnerIntent = false
  notifyIntentListeners()
}

/** Build register-owner path with email/mode in the URL (payload stays in router state). */
export function buildOwnerRegisterPath(options?: {
  email?: string
  mode?: 'register' | 'login'
}): string {
  const params = new URLSearchParams()
  const email = options?.email?.trim().toLowerCase()
  if (email) params.set('email', email)
  if (options?.mode === 'login') params.set('mode', 'login')
  const qs = params.toString()
  return qs ? `/create-ad/register-owner?${qs}` : '/create-ad/register-owner'
}

/** Navigate to a blank create-ad wizard (clears stale edit/draft session). */
export const CREATE_AD_FRESH_STATE = { newListing: true as const }

export function buildCreateAdPath(): string {
  return '/create-ad'
}

/** Build published-result path with listing id in the URL. */
export function buildPublishedPath(
  listingId: string,
  mode: 'created' | 'updated' = 'created'
): string {
  const params = new URLSearchParams()
  params.set('listingId', String(listingId).trim())
  if (mode === 'updated') params.set('mode', 'updated')
  return `/create-ad/published?${params.toString()}`
}

/** Account / verify-email continuation with optional listing focus. */
export function buildAccountPath(listingId?: string | null): string {
  const id = listingId?.trim()
  return id ? `/account?listingId=${encodeURIComponent(id)}` : '/account'
}

export function buildVerifyEmailPath(nextPath: string): {
  pathname: string
  state: { next: string }
} {
  return {
    pathname: '/verify-email',
    state: { next: nextPath },
  }
}
